import { useContext, useEffect, useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { modelStrategyApi } from '@/api/modelStrategy';
import { SocketContext } from '@/contexts/rtm';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { selectUserId } from '@/redux/user/selectors';
import { emptyStrategy, ModelStrategy } from '@/shared/types/modelStrategy';
import { actions } from '@/shared/types/permissions';
import { ModelStrategyReplaced, SocketEvent } from '@/shared/types/rtm';
import { RootState } from '@/shared/types/state';

export const modelStrategyQueryKey = (brainId: string) =>
  ['brains', brainId, 'model-strategy'] as const;

/**
 * Brain-scoped model strategy hook. Strategy is read and written as a single
 * nested document — server is the canonical owner of structure, client just
 * mirrors it. `saveDraft` PATCHes the whole strategy and seeds the cache
 * from the response; `clearAll` DELETEs and resets the cache.
 */
export const useModelStrategy = (brainId: string | undefined) => {
  const queryClient = useQueryClient();
  const accountId = useSelector(selectAccountId);
  const userId = useSelector(selectUserId);
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'model_strategy', actions.READ)
  );
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'model_strategy', actions.WRITE)
  );
  const { socket, isConnected } = useContext(SocketContext);

  const queryKey = useMemo(() => modelStrategyQueryKey(brainId ?? '-'), [brainId]);

  const {
    data,
    status: getStatus,
    refetch,
  } = useQuery<ModelStrategy>({
    queryKey,
    enabled: !!accountId && !!brainId && canRead,
    queryFn: () => modelStrategyApi.get(brainId!),
  });

  const strategy = data ?? emptyStrategy();

  const { mutateAsync: saveDraftAsync, status: saveStatus } = useMutation<
    ModelStrategy,
    Error,
    ModelStrategy
  >({
    mutationFn: async (nextDraft) => {
      if (!brainId) {
        throw new Error('brainId is required');
      }
      const persisted = await modelStrategyApi.patch(brainId, nextDraft);
      queryClient.setQueryData<ModelStrategy>(queryKey, persisted);
      return persisted;
    },
  });

  const { mutateAsync: clearAllAsync, status: clearStatus } = useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!brainId) {
        throw new Error('brainId is required');
      }
      await modelStrategyApi.remove(brainId);
      queryClient.setQueryData<ModelStrategy>(queryKey, emptyStrategy());
    },
  });

  // Subscribe to brain-scoped strategy events. Skip our own writes (already
  // applied via saveDraft) and events for other brains. The payload only
  // carries `brain_id`, so refetch to pick up the new strategy.
  useEffect(() => {
    if (!socket || !brainId || !isConnected) {
      return;
    }

    const handleReplaced = (payload: { user_id: string; data?: ModelStrategyReplaced }) => {
      if (payload.user_id === userId) {
        return;
      }
      if (!payload.data || payload.data.brain_id !== brainId) {
        return;
      }
      queryClient.invalidateQueries({ queryKey });
    };

    socket.on(SocketEvent.model_strategy_replaced, handleReplaced);
    return () => {
      socket.off(SocketEvent.model_strategy_replaced, handleReplaced);
    };
  }, [socket, isConnected, brainId, userId, queryClient, queryKey]);

  return {
    /** Persisted strategy as last fetched. */
    strategy,
    getStatus,
    refetch,
    saveDraft: saveDraftAsync,
    saveStatus,
    clearAll: clearAllAsync,
    clearStatus,
    canRead,
    canWrite,
  };
};
